// DirtyMarksCard.tsx — the selected experiment's dirty marks: list them,
// create one from the graph selection, unmark or delete one.
//
// A dirty mark says an experiment's values at some commits are not
// comparable to the rest (a changed eval protocol, leakage, a scorer bug, no
// holdout). It is a rule the server evaluates on every read, so it covers
// values posted later too. Two shapes, both picked from the commits selected
// in the graph:
//
//   older than  — the ancestors of one commit (optionally the commit too);
//   range       — git's from..to: reachable from `to`, not from `from`.
//
// Routes: GET/POST /experiments/{slug}/dirty, POST …/dirty/{id}/unmark
// (increment.use), DELETE …/dirty/{id} (increment.admin). Unmark keeps the
// mark as history; delete erases it.
import { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { BsTrash } from 'react-icons/bs';
import { StatusBadge, unwrapJson, ui } from 'common';
import { apiClient } from '../api';
import type { DirtyMark, DirtyMarksResponse } from '../api';
import { shortSha } from './format';

interface Props {
  owner: string;
  name: string;
  /** Selected experiment slug; '' = none, and the card says so. */
  experiment: string;
  /** Commits selected in the graph, in pick order — the mark's boundaries. */
  selection: string[];
  canWrite: boolean;
  isAdmin: boolean;
  /** Bumped by the page when anything else changed marks. */
  reloadKey: number;
  /** Called after a mark was created, unmarked or deleted. */
  onChanged: () => void;
}

type Mode = 'before' | 'range';

const MODE_OPTIONS: { value: Mode; label: string }[] = [
  { value: 'before', label: 'older than a commit' },
  { value: 'range', label: 'a range from..to' },
];

function ruleText(m: DirtyMark): string {
  if (m.kind === 'before') {
    return `older than ${shortSha(m.before ?? '')}${m.include_commit ? ', inclusive' : ''}`;
  }
  return `${shortSha(m.from ?? '')}..${shortSha(m.to ?? '')}`;
}

export default function DirtyMarksCard({
  owner, name, experiment, selection, canWrite, isAdmin, reloadKey, onChanged,
}: Props) {
  const [marks, setMarks] = useState<DirtyMark[]>([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<Mode>('before');
  const [boundary, setBoundary] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [inclusive, setInclusive] = useState(false);
  const [metrics, setMetrics] = useState('');
  const [reason, setReason] = useState('');
  const [confirm, confirmDialog] = ui.useConfirm();
  // Unmarked marks are history; they stay folded away unless asked for.
  const [showUnmarked, setShowUnmarked] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toggleReason = (id: number) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const base = `/api/v1/repos/${owner}/${name}/experiments/${encodeURIComponent(experiment)}/dirty`;

  useEffect(() => {
    if (!experiment) {
      setMarks([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.fetch(base);
        const body = await unwrapJson<DirtyMarksResponse>(res);
        if (!cancelled) {
          setMarks(body.marks ?? []);
          setError('');
        }
      } catch (err) {
        if (!cancelled) {
          setMarks([]);
          setError(err instanceof Error ? err.message : 'Failed to load dirty marks');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [base, experiment, reloadKey]);

  // Boundaries default from the selection: the first pick for "older than",
  // the first two picks as from..to. A boundary that left the selection
  // falls back to the default rather than lingering.
  const selKey = selection.join(',');
  useEffect(() => {
    setBoundary((b) => (selection.includes(b) ? b : selection[0] ?? ''));
    setFrom((f) => (selection.includes(f) ? f : selection[0] ?? ''));
    setTo((t) => (selection.includes(t) ? t : selection[1] ?? ''));
    if (selection.length >= 2) setMode('range');
    else setMode('before');
    // selKey stands in for selection (a fresh array every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selKey]);

  const shaOptions = selection.map((s) => ({ value: s, label: shortSha(s) }));
  const ready = reason.trim() !== '' && (mode === 'before'
    ? boundary !== ''
    : from !== '' && to !== '' && from !== to);

  const create = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const names = metrics.split(',').map((m) => m.trim()).filter(Boolean);
      const body = mode === 'before'
        ? { before: boundary, include_commit: inclusive, metrics: names, reason: reason.trim() }
        : { from, to, metrics: names, reason: reason.trim() };
      const res = await apiClient.fetch(base, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const mark = await unwrapJson<DirtyMark>(res);
      setNotice(`Mark #${mark.id} covers ${mark.affected_values} value${mark.affected_values === 1 ? '' : 's'}`
        + ` and ${mark.affected_feeds} feed${mark.affected_feeds === 1 ? '' : 's'}.`);
      setReason('');
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create the mark');
    } finally {
      setBusy(false);
    }
  };

  const unmark = async (m: DirtyMark) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await apiClient.fetch(`${base}/${m.id}/unmark`, { method: 'POST' });
      await unwrapJson<DirtyMark>(res);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unmark');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m: DirtyMark) => {
    const ok = await confirm({
      title: 'Delete Dirty Mark',
      body: `Delete mark #${m.id} (${ruleText(m)}) and its history? Unmarking keeps the record; deleting does not.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await apiClient.fetch(`${base}/${m.id}`, { method: 'DELETE' });
      if (!res.ok) await unwrapJson<unknown>(res);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete the mark');
    } finally {
      setBusy(false);
    }
  };

  const inactiveCount = marks.filter((m) => !m.active).length;
  const shownMarks = showUnmarked ? marks : marks.filter((m) => m.active);

  return (
    <div className="card">
      <div className="card-header d-flex flex-wrap align-items-center gap-2">
        <strong>Dirty Marks</strong>
        <span className="text-secondary">
          {marks.filter((m) => m.active).length} active
        </span>
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger">{error}</div>}
        {notice && <div className="alert alert-success">{notice}</div>}
        {!experiment ? (
          <div className="empty-state">Select an experiment to see its dirty marks.</div>
        ) : marks.length === 0 ? (
          <div className="text-secondary mb-3">
            No dirty marks. Values this experiment marks dirty are struck
            through and never count toward a best, headline or trend.
          </div>
        ) : (
          <>
            <ul className="dirty-mark-list">
              {shownMarks.map((m) => (
                <li key={m.id} className={m.active ? undefined : 'dirty-mark-off'}>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <code>{ruleText(m)}</code>
                    <span className="text-secondary">
                      {m.metrics.length === 0 ? 'all metrics' : m.metrics.map((n) => <code key={n} className="me-1">{n}</code>)}
                    </span>
                    {!m.active && (
                      <StatusBadge tone="neutral" uppercase={false}>unmarked</StatusBadge>
                    )}
                    <span
                      className="text-secondary ms-auto text-nowrap"
                      title={[
                        `mark #${m.id} by ${m.marked_by} ${new Date(m.marked_at).toLocaleString()}`,
                        m.unmarked_at ? `unmarked by ${m.unmarked_by} ${new Date(m.unmarked_at).toLocaleString()}` : '',
                      ].filter(Boolean).join('\n')}
                    >
                      {m.affected_values} values · {m.affected_feeds} feeds · {new Date(m.marked_at).toLocaleDateString()}
                    </span>
                    {canWrite && m.active && (
                      <Button variant="link" size="sm" className="p-0" disabled={busy} onClick={() => unmark(m)}>
                        Unmark
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-danger"
                        disabled={busy}
                        aria-label={`Delete mark #${m.id}`}
                        onClick={() => remove(m)}
                      >
                        <BsTrash />
                      </Button>
                    )}
                  </div>
                  {/* Reasons run to paragraphs; two lines until clicked. */}
                  <button
                    type="button"
                    className={`dirty-mark-reason${expanded.has(m.id) ? ' dirty-mark-reason-open' : ''}`}
                    onClick={() => toggleReason(m.id)}
                    aria-expanded={expanded.has(m.id)}
                  >
                    {m.reason}
                  </button>
                </li>
              ))}
            </ul>
            {inactiveCount > 0 && (
              <Button variant="link" size="sm" className="p-0 mb-2" onClick={() => setShowUnmarked((v) => !v)}>
                {showUnmarked ? 'Hide' : 'Show'} {inactiveCount} unmarked
              </Button>
            )}
          </>
        )}
        {experiment && canWrite && (
          <div className="d-flex flex-column gap-2 mt-2">
            <h2 className="h6 m-0">New Mark</h2>
            {selection.length === 0 ? (
              <div className="text-secondary">
                Select a commit in the graph to mark everything older than it,
                or two (ctrl/cmd/shift-click) to mark a range.
              </div>
            ) : (
              <>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <span className="text-secondary">Values</span>
                  <ui.Dropdown
                    value={mode}
                    minWidth={18}
                    options={selection.length >= 2 ? MODE_OPTIONS : MODE_OPTIONS.slice(0, 1)}
                    onChange={(e) => setMode(e.target.value as Mode)}
                  />
                  {mode === 'before' ? (
                    <>
                      <ui.Dropdown
                        value={boundary}
                        minWidth={8}
                        options={shaOptions}
                        onChange={(e) => setBoundary(e.target.value)}
                      />
                      <div className="form-check m-0">
                        <input
                          id="dirty-inclusive"
                          className="form-check-input"
                          type="checkbox"
                          checked={inclusive}
                          onChange={(e) => setInclusive(e.target.checked)}
                        />
                        <label htmlFor="dirty-inclusive" className="form-check-label">
                          and the commit itself
                        </label>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="text-secondary">from (excluded)</span>
                      <ui.Dropdown
                        value={from}
                        minWidth={8}
                        options={shaOptions}
                        onChange={(e) => setFrom(e.target.value)}
                      />
                      <span className="text-secondary">to (included)</span>
                      <ui.Dropdown
                        value={to}
                        minWidth={8}
                        options={shaOptions}
                        onChange={(e) => setTo(e.target.value)}
                      />
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => { setFrom(to); setTo(from); }}
                      >
                        Swap
                      </Button>
                    </>
                  )}
                </div>
                <label className="d-flex flex-column gap-1">
                  <span className="text-secondary">Metrics — comma-separated; empty means every metric</span>
                  <ui.Text
                    value={metrics}
                    placeholder="all metrics"
                    onChange={(e) => setMetrics(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </label>
                <label className="d-flex flex-column gap-1">
                  <span className="text-secondary">Reason (required)</span>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={reason}
                    placeholder="Why these values are not comparable — e.g. eval protocol changed, no holdout"
                    onChange={(e) => setReason(e.target.value)}
                  />
                </label>
                <div>
                  <Button variant="primary" disabled={busy || !ready} onClick={create}>
                    Mark dirty
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {confirmDialog}
    </div>
  );
}
